import React from 'react';
import ReactECharts from 'echarts-for-react';
import { Stack } from 'react-bootstrap';
import { BOOTSTRAP_COLORS } from '@/lomavisSharedUi/colors';
import { getDateTimeFormated } from '@/components/utils/dates/utils';

interface DataPoint {
  x: any; // date-like (string | number | Date)
  y: number;
}
interface SeriesData {
  name: string;
  data: DataPoint[];
}
interface LomavisLineChart1Props {
  title?: string;
  data: SeriesData[];
  width?: string | number;
  height?: string | number;
  smooth?: boolean;
  lineColor?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  showGrid?: boolean;
  showTooltip?: boolean;
}

/* ----- helpers ----- */

// parse different kinds of x values into a valid Date or null
function parseToDate(val: any): Date | null {
  if (val == null) return null;
  if (val instanceof Date) {
    return isNaN(val.getTime()) ? null : new Date(val.getTime());
  }
  if (typeof val === 'number') {
    // if it's probably seconds (10 digits) convert to ms
    const ts = val < 1e12 ? val * 1000 : val;
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof val === 'string') {
    // try direct parse
    const d = new Date(val);
    if (!isNaN(d.getTime())) return d;
    // try numeric-string timestamp
    const n = Number(val);
    if (!isNaN(n)) {
      const ts = n < 1e12 ? n * 1000 : n;
      const d2 = new Date(ts);
      return isNaN(d2.getTime()) ? null : d2;
    }
  }
  return null;
}

// format to YYYY-MM-DD using local date (avoids UTC shifting)
function formatYMD(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// inclusive date range using local dates
function getDateRangeYMD(start: Date, end: Date) {
  const arr: string[] = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  // eslint-disable-next-line no-unmodified-loop-condition
  while (cur <= last) {
    arr.push(formatYMD(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return arr;
}

/* ----- component ----- */

export const LomavisLineChart1: React.FC<LomavisLineChart1Props> = ({
  title,
  data,
  width = '100%',
  height = 500,
  smooth = true,
  xAxisLabel,
  yAxisLabel,
  showGrid = true,
  showTooltip = true
}) => {
  const MAX_ITEMS_BEFORE_SCROLL = 10;
  // collect all valid dates across all series
  const allParsedDates: Date[] = [];
  data.forEach((series) =>
    series.data.forEach((p) => {
      const d = parseToDate(p.x);
      if (d) allParsedDates.push(d);
    })
  );

  // if no valid dates, fall back to original category extraction (prevents empty chart)
  let categories: string[] = [];
  if (allParsedDates.length === 0) {
    // fallback: use original strings (unique & sorted)
    categories = Array.from(
      new Set(data.flatMap((s) => s.data.map((p) => String(p.x))))
    ).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  } else {
    const minDate = new Date(
      Math.min(...allParsedDates.map((d) => d.getTime()))
    );
    const maxDate = new Date(
      Math.max(...allParsedDates.map((d) => d.getTime()))
    );
    categories = getDateRangeYMD(minDate, maxDate);
  }

  // Build series — for each series, map date->value then produce array matching categories.
  const series = data.map((seriesItem, idx) => {
    const map = new Map<string, number>();

    // sum duplicate x values into the same date bucket
    seriesItem.data.forEach((p) => {
      const d = parseToDate(p.x);
      const key = d ? formatYMD(d) : String(p.x);
      const y = typeof p.y === 'number' ? p.y : Number(p.y) || 0;
      map.set(key, (map.get(key) || 0) + y);
    });

    const seriesData = categories.map((cat) => map.get(cat) ?? 0);

    return {
      name: seriesItem.name,
      type: 'line',
      smooth,
      data: seriesData,
      lineStyle: {
        color: BOOTSTRAP_COLORS[idx % BOOTSTRAP_COLORS.length],
        width: 2
      },
      itemStyle: {
        color: BOOTSTRAP_COLORS[idx % BOOTSTRAP_COLORS.length]
      },
      symbol: 'circle',
      symbolSize: 4
    };
  });

  const option = {
    tooltip: showTooltip
      ? {
          trigger: 'axis'
        }
      : undefined,
    legend: {
      type: data.length > MAX_ITEMS_BEFORE_SCROLL ? 'scroll' : 'plain',
      orient: 'horizontal',
      top: 'top',
      data: data.map((s) => s.name)
    },

    grid: showGrid
      ? {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true,
          show: true,
          borderColor: '#e0e0e0'
        }
      : {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
    xAxis: {
      type: 'category',
      data: categories,
      name: xAxisLabel,
      nameLocation: 'middle',
      nameGap: 30,
      axisLine: { lineStyle: { color: '#666' } },
      axisLabel: {
        formatter: (value: string) => {
          // value here is each category string like "2025-09-15"
          const d = new Date(value);

          return getDateTimeFormated(d, true);
        }
      }
    },
    yAxis: {
      type: 'value',
      name: yAxisLabel,
      nameLocation: 'middle',
      nameGap: 40,
      nameRotate: 90,
      axisLine: { lineStyle: { color: '#666' } }
    },
    series
  };

  return (
    <Stack>
      <ReactECharts
        option={option}
        style={{ width, height }}
        notMerge
        lazyUpdate
      />
    </Stack>
  );
};
